// Navbar.jsx
import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import logo from "../assets/logo.png";

export default function Navbar() {
  // ✅ Utiliser isAdmin() au lieu de vérifier user.role manuellement
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b ${
        theme === "dark"
          ? "border-slate-800 bg-slate-900/80"
          : "border-slate-200 bg-white/80"
      } backdrop-blur`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="Moroccan Desert Trek" className="h-8 w-10" />
          <span
            className={`text-lg font-bold ${
              theme === "dark" ? "text-slate-100" : "text-slate-800"
            }`}
          >
            Moroccan Desert Trek
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 md:flex">
          {[
            { to: "/tours", label: "Tours" },
            { to: "/about", label: "About" },
            { to: "/contact", label: "Contact" },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `text-sm ${
                  isActive
                    ? theme === "dark"
                      ? "text-emerald-400"
                      : "text-emerald-600"
                    : theme === "dark"
                    ? "text-slate-300 hover:text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {isAuthenticated && !isAdmin() && (
            <>
              <NavLink
                to="/my-bookings"
                className={({ isActive }) =>
                  `text-sm ${
                    isActive
                      ? theme === "dark"
                        ? "text-emerald-400"
                        : "text-emerald-600"
                      : theme === "dark"
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-600 hover:text-slate-900"
                  }`
                }
              >
                My Bookings
              </NavLink>
              <NavLink
                to="/profile"
                className={({ isActive }) =>
                  `text-sm ${
                    isActive
                      ? theme === "dark"
                        ? "text-emerald-400"
                        : "text-emerald-600"
                      : theme === "dark"
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-600 hover:text-slate-900"
                  }`
                }
              >
                Profile
              </NavLink>
            </>
          )}

          {/* ✅ CORRECTION : Utiliser isAdmin() au lieu de user?.role === "admin" */}
          {isAdmin() && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `text-sm ${
                  isActive
                    ? theme === "dark"
                      ? "text-emerald-400"
                      : "text-emerald-600"
                    : theme === "dark"
                    ? "text-slate-300 hover:text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        {/* Actions (Theme, Login, etc.) */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`rounded-md border px-2 py-1 text-xs ${
              theme === "dark"
                ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                : "border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
          </button>

          {!isAuthenticated ? (
            <div className="hidden gap-2 md:flex">
              <Link
                to="/login"
                className={`rounded-md px-3 py-1.5 text-sm ${
                  theme === "dark"
                    ? "text-slate-200 hover:bg-slate-800"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                Login
              </Link>
              <Link
                to="/register"
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700"
              >
                Sign Up
              </Link>
            </div>
          ) : (
            <button
              onClick={logout}
              className={`hidden md:block rounded-md px-3 py-1.5 text-sm ${
                theme === "dark"
                  ? "bg-slate-100 text-slate-900 hover:bg-white"
                  : "bg-slate-900 text-white hover:bg-slate-700"
              }`}
            >
              Logout
            </button>
          )}

          {/* Hamburger Menu */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            className={`md:hidden rounded-md border px-2 py-1 ${
              theme === "dark"
                ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                : "border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className={`border-t px-4 py-3 md:hidden ${
            theme === "dark"
              ? "border-slate-800 bg-slate-900"
              : "border-slate-200 bg-white"
          }`}
        >
          <nav className="flex flex-col gap-3">
            {[
              { to: "/tours", label: "Tours" },
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
            ].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md ${
                    isActive
                      ? theme === "dark"
                        ? "bg-emerald-900/20 text-emerald-400"
                        : "bg-emerald-50 text-emerald-600"
                      : theme === "dark"
                      ? "text-slate-300 hover:bg-slate-800"
                      : "text-slate-600 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            {isAuthenticated && !isAdmin() && (
              <>
                <NavLink
                  to="/my-bookings"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm rounded-md ${
                      isActive
                        ? theme === "dark"
                          ? "bg-emerald-900/20 text-emerald-400"
                          : "bg-emerald-50 text-emerald-600"
                        : theme === "dark"
                        ? "text-slate-300 hover:bg-slate-800"
                        : "text-slate-600 hover:bg-slate-50"
                    }`
                  }
                >
                  My Bookings
                </NavLink>
                <NavLink
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm rounded-md ${
                      isActive
                        ? theme === "dark"
                          ? "bg-emerald-900/20 text-emerald-400"
                          : "bg-emerald-50 text-emerald-600"
                        : theme === "dark"
                        ? "text-slate-300 hover:bg-slate-800"
                        : "text-slate-600 hover:bg-slate-50"
                    }`
                  }
                >
                  Profile
                </NavLink>
              </>
            )}

            {/* ✅ CORRECTION : Utiliser isAdmin() au lieu de user?.role === "admin" */}
            {isAdmin() && (
              <NavLink
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md ${
                    isActive
                      ? theme === "dark"
                        ? "bg-emerald-900/20 text-emerald-400"
                        : "bg-emerald-50 text-emerald-600"
                      : theme === "dark"
                      ? "text-slate-300 hover:bg-slate-800"
                      : "text-slate-600 hover:bg-slate-50"
                  }`
                }
              >
                Admin
              </NavLink>
            )}

            <div
              className={`mt-2 border-t pt-3 ${
                theme === "dark" ? "border-slate-700" : "border-slate-200"
              }`}
            >
              {!isAuthenticated ? (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`rounded-md px-3 py-2 text-center text-sm ${
                      theme === "dark"
                        ? "text-slate-200 hover:bg-slate-800"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-md bg-emerald-600 px-3 py-2 text-center text-sm text-white hover:bg-emerald-700"
                  >
                    Sign Up
                  </Link>
                </div>
              ) : (
                <button
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full rounded-md px-3 py-2 text-sm ${
                    theme === "dark"
                      ? "bg-slate-100 text-slate-900 hover:bg-white"
                      : "bg-slate-900 text-white hover:bg-slate-700"
                  }`}
                >
                  Logout
                </button>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
